<script setup>
import {ref, computed, onMounted, watch, reactive, readonly} from "vue";
import { useI18n } from "vue-i18n";
import { handleError } from '@/utils/errors';
import { computeClass } from "@/utils/styleFormats.js";
import BaseTable from "@/components/tables/BaseTable.vue";
import {IXCCustomerEntity} from "@/entities/IXCCustomerEntity.js";
import BasePage from "@/components/Layouts/BasePage.vue";
import {getAvailableActionsForRoute} from "@/services/api/permissionManagement.js";
import ClientCard from "@/components/common/ClientCard.vue";
import StatsGrid from "@/components/common/Filters/StatsGrid.vue";
import Text from "@/components/common/Text.vue";
import BaseAlert from "@/components/common/alerts/BaseAlert.vue";
import {getPerson} from "@/services/api/person.js";
import {CustomerStep} from "@/enums/enums.js";
import {dateFormatterInput, formatDateShortBR} from "@/utils/dateTimeFormat.js";
import {useTablePagination} from "@/js/composables/useTablePagination.js";
import {getAnnotationById} from "@/services/api/annotation.js";
import {useRoute} from "vue-router";
import {getContractById, getContracts} from "@/services/api/contracts.js";
import {formatBytesToMB, formatNumber} from "@/utils/numberFormat.js";
import GroupList from "@/components/common/GroupList.vue";

const route = useRoute();
const { t, locale } = useI18n();
const ixcContractsEntity = new IXCCustomerEntity();
const entity = ref(ixcContractsEntity);
const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const filters = ref([]);
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);
const actions = ref([]);
const havePermission = ref(false);
const orderBy = ref(null);
const step = ref(1);
const tchenetHistory = ref(null);
const contract = ref(null);
const customer = ref(null);
const id = route.params.id;
const listContracts = ref(null);
const openInvoices = ref(null);
const showAlert = ref(null)
const document = route.query.document;
const selectedContractId = ref(null);

const billing = computed(() =>
    (contract.value?.faturamento || [])
        .filter(f => f.valor)
        .map(f => ({
        ...f,
        payment_date: f.data_pagamento,
        amount: f.valor
    }))
);

const equipments = computed(() =>
    (contract.value?.comodato || [])
        .filter(c => c.st_comodato_emp_dev_baixa)
        .map(c => ({
            ...c,
            status: c.st_comodato_emp_dev_baixa
        }))
);

const stats = computed(() =>
    generateStats({
        billing: billing.value,
        equipments: equipments.value,
        tickets: contract.value?.atendimento || []
    })
);

function generateStats({ billing = [], equipments = [], tickets = [] }) {
    openInvoices.value = billing.filter(b =>
        b.status !== "R"
    );
    const totalOpenAmount = openInvoices.value.reduce((acc, b) => {
        const value = parseFloat((b.amount || "0").replace(",", "."));
        return acc + value;
    }, 0);

    const formatCurrency = (value) => {
        return value.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL"
        });
    };

    const totalEquipments = equipments.length;
    const comodatoCount = equipments.filter(
        e => e.status === "COMODATO"
    ).length;

    const soldCount = equipments.filter(
        e => e.status === "VENDIDO"
    ).length;

    const parseBRDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        const [day, month, year] = dateStr.split("/");
        return new Date(`${year}-${month}-${day}`);
    };

    let lastTicket = null;

    if (tickets.length) {
        lastTicket = [...tickets].sort((a, b) =>
            parseBRDate(b.data_ultima_alteracao) - parseBRDate(a.data_ultima_alteracao)
        )[0];
    }

    const lastTicketValue = lastTicket?.data_ultima_alteracao
        ? dateFormatterInput(lastTicket.data_ultima_alteracao)
        : t("components.customers.index.noRecord");

    const lastTicketSubtitle = lastTicket
        ? t("components.customers.index.protocol", { protocol: lastTicket.protocolo ?? "0" })
        : t("components.customers.index.noSupport");

    const downloadBytes = Number(contract.value?.ultimo_login?.[0]?.download ?? 0);
    const uploadBytes = Number(contract.value?.ultimo_login?.[0]?.upload ?? 0);

    const downloadMB = formatBytesToMB(downloadBytes);
    const uploadMB = formatBytesToMB(uploadBytes);

    return [
        {
            title: t("components.customers.index.openInvoices"),
            value: formatCurrency(totalOpenAmount),
            subtitle: t("components.customers.index.pendingInvoices", { count: openInvoices.value.length }),
            icon: "mdi-credit-card-outline",
            type: openInvoices.value.length ? "error" : "success"
        },
        {
            title: t("components.customers.index.consumption"),
            value: t("components.customers.index.downloadValue", { value: downloadMB }),
            subtitle: t("components.customers.index.uploadValue", { value: uploadMB }),
            icon: "mdi-wifi",
            type: "success"
        },
        {
            title: t("components.customers.index.equipment"),
            value: t("components.customers.index.units", { count: totalEquipments }),
            subtitle: t("components.customers.index.equipmentSummary", { comodato: comodatoCount, sold: soldCount }),
            icon: "mdi-monitor"
        },
        {
            title: t("components.customers.index.lastSupport"),
            value: lastTicketValue ?? t("components.customers.index.noRecord"),
            subtitle: lastTicketSubtitle,
            icon: "mdi-headset"
        }
    ];
}

const supportItems = computed(() => {
    return (contract.value?.atendimento || []).map(item => ({
        ...item,
        data_criacao: dateFormatterInput(item.data_criacao),
        data_ultima_alteracao: dateFormatterInput(item.data_ultima_alteracao)
    }));
});

const equipamentItems = computed(() => {
    return (contract.value?.comodato || []).map(item => ({
        ...item,
        data_movimentacao: dateFormatterInput(item.data_movimentacao),
        valor_unitario: formatNumber(item.valor_unitario, locale.value),
        valor_total: formatNumber(item.valor_total, locale.value),
        quantidade: formatNumber(item.quantidade, locale.value, 0)
    }));
});

const invoicesItems = computed(() => {

    const documentFilter = route.query.document;

    const nfs = contract.value?.nfs || [];

    const grouped = nfs.reduce((acc, item) => {

        if (!acc[item.id_nf]) {
            acc[item.id_nf] = {
                ...item,
                data_emissao: dateFormatterInput(item.data_emissao),
                tipo_documento: item.tipo_documento,
                valor_nf: formatNumber(item.valor_nf, locale.value),
                modelo_nf: item.modelo_nf,
                items: []
            };
        }

        acc[item.id_nf].items.push({
            valor_unitario: formatNumber(item.valor_unitario, locale.value),
            descricao: item.descricao,
            valor_total_produto: formatNumber(item.valor_total_produto, locale.value)
        });

        return acc;

    }, {});

    let result = Object.values(grouped);

    if (documentFilter) {
        result = result.filter(item =>
            String(item.id_nf).includes(String(documentFilter))
        );
    }

    return result;
});

function hasLastLoginValue(value) {
    return value !== null && value !== undefined && value !== "" && value !== "0000-00-00";
}

function formatLastLoginValue(value, formatter = item => item) {
    if (!hasLastLoginValue(value)) {
        return t("components.customers.index.noRecord");
    }

    return formatter(value);
}

// Dados para a aba "Último login"
const lastLoginInfo = computed(() => {
    const u = contract.value?.ultimo_login?.[0] || null;

    if (!u) return null;

    const login = u.login;
    const onu_mac = u.onu_mac || u.onuMac;

    // utilizar o mesmo tratamento de bytes já usado nos cards (MB/GB)
    const downloadStr = formatLastLoginValue(u.download, value => formatBytesToMB(Number(value)));
    const uploadStr = formatLastLoginValue(u.upload, value => formatBytesToMB(Number(value)));

    const ini = u.ultima_conexao_inicial || u.data_conexao_inicial || u.conexao_inicial || null;
    const fim = u.ultima_conexao_final || u.data_conexao_final || u.conexao_final || null;
    const cancel = u.data_cancelamento || null;
    const result = {
        login: formatLastLoginValue(login),
        onu_mac: formatLastLoginValue(onu_mac),
        downloadStr,
        uploadStr,
        ini: formatLastLoginValue(ini, dateFormatterInput),
        fim: formatLastLoginValue(fim, dateFormatterInput),
        cancel: formatLastLoginValue(cancel, dateFormatterInput)
    };

    return result;
});

const invoicingItems = computed(() => {
    return (contract.value?.faturamento || []).map(item => ({
        ...item,
        document: item.documento,
        data_emissao: dateFormatterInput(item.data_emissao),
        data_vencimento: dateFormatterInput(item.data_vencimento),
        valor: formatNumber(item.valor, locale.value),
        valor_recebido: formatNumber(item.valor_recebido, locale.value)
    }));
});

const {
    paginatedItems: paginatedSupport,
    totalInfo: totalSupport,
    currentPage: currentPageSupport,
    itemsPerPage: itemsPerPageSupport,
    onPageChange: onPageChangeSupport
} = useTablePagination({
    items: supportItems,
    page: 1,
    perPage: 10
});

const {
    paginatedItems: paginatedEquipament,
    totalInfo: totalEquipament,
    currentPage: currentPageEquipament,
    itemsPerPage: itemsPerPageEquipament,
    onPageChange: onPageChangeEquipament
} = useTablePagination({
    items: equipamentItems,
    page: 1,
    perPage: 10
});


const {
    paginatedItems: paginatedInvoices,
    totalInfo: totalInvoices,
    currentPage: currentPageInvoices,
    itemsPerPage: itemsPerPageInvoices,
    onPageChange: onPageChangeInvoices
} = useTablePagination({
    items: invoicesItems,
    page: 1,
    perPage: 10
});

const {
    paginatedItems: paginatedInvoicing,
    totalInfo: totalInvoicing,
    currentPage: currentPageInvoicing,
    itemsPerPage: itemsPerPageInvoicing,
    onPageChange: onPageChangeInvoicing
} = useTablePagination({
    items: invoicingItems,
    page: 1,
    perPage: 10
});

async function fetchData(params = {}) {
    try {
        loading.value = true;
        const contractPersonResponse = await getContracts({
            customer_id: params.id ?? id
        });
        customer.value = contractPersonResponse.data[0].customer;
        listContracts.value = contractPersonResponse.data;

        await fetchContractNote(contractPersonResponse?.data[0]?.id);
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

async function fetchContractNote(contractId) {
    if (!contractId || selectedContractId.value === contractId) {
        return;
    }

    const ContractNoteResponse = await getContractById({
        id: contractId + '/note'
    });

    const contractNote = JSON.parse(ContractNoteResponse.data[0].note);
    tchenetHistory.value = contractNote.historicos;
    contract.value = contractNote;
    selectedContractId.value = contractId;
}


async function onOrderBy(orderByArray) {
    orderBy.value = orderByArray;
}

onMounted(async () => {
    actions.value = await getAvailableActionsForRoute(entity.value.APIRoute);
    showAlert.value = true;
    if (!actions.value?.list?.length) {
        message.error(t("common.messages.errors.403"));
        return;
    }

    havePermission.value = true;

    await fetchData();

    if (route.query.tab) {
        step.value = parseInt(route.query.tab);
    }

});

async function handleSelectedContract(contractSelected) {
    try {
        loading.value = true;
        await fetchContractNote(contractSelected?.id);
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

watch(
    () => route.query,
    async (query) => {

        if (query.tab) {
            step.value = parseInt(query.tab);
        }

    },
    { immediate: true }
);

</script>

<template>
    <BasePage :entity="entity">
        <template #default>
            <v-row cols="12">
                <v-col>
                    <ClientCard
                        v-if=contract
                        :key="customer.value?.id"
                        :client="customer"
                        :history="tchenetHistory"
                        :listContracts="listContracts"
                        @update:contract="handleSelectedContract"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col
                    v-for="(item, index) in stats"
                    :key="index"
                    class="mb-6"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                >
                    <StatsGrid
                        v-bind="item"
                    />
                </v-col>
            </v-row>
            <BaseAlert
                v-model="showAlert"
                type="warning"
                class="mb-5"
                v-if="openInvoices?.length"
                :title="t('components.customers.index.legacyMigrationWarning')"
                :text="t('components.customers.index.legacyMigrationWarningText', { count: openInvoices?.length })"
                :buttonText="t('components.customers.index.collapse')"
                :showButton="true"
            />
            <v-tabs v-model="step" class="mb-5">
                <v-tab class=" button-route me-4 text-dark" :value="CustomerStep.Invoicing">
                    <span>{{ t("components.customers.index.invoicing") }}</span>
                </v-tab>
                <v-tab class=" button-route me-4 text-dark" :value="CustomerStep.Invoices">
                    <span>{{ t("components.customers.index.invoices") }}</span>
                </v-tab>
                <v-tab class=" button-route me-4 text-dark" :value="CustomerStep.Equipment">
                    <span>{{ t("components.customers.index.equipment") }}</span>
                </v-tab>
                <v-tab class=" button-route me-4 text-dark" :value="CustomerStep.Support">
                    <span>{{ t("components.customers.index.support") }}</span>
                </v-tab>
                <v-tab class=" button-route me-4 text-dark" :value="CustomerStep.LastLogin">
                    <span>{{ t("components.customers.index.lastLogin") }}</span>
                </v-tab>
            </v-tabs>
            <v-window  v-if="contract" v-model="step">
                <v-window-item :value="CustomerStep.Invoicing">
                    <v-card class="border border-top-radius h-45 pt-3 " >
                        <Text class=" me-2" tag="h5" weight="medium">
                        <v-icon color="indigo" class="me-1">mdi-clipboard-text-outline</v-icon> {{ t("components.customers.index.financialInvoicing") }}</Text>
                    </v-card>
                    <BaseTable
                        :fetchData="null"
                        :entity="entity"
                        :items="paginatedInvoicing"
                        :loading="loading"
                        :totalItems="totalInvoicing"
                        :currentPage="currentPageInvoicing"
                        :itemsPerPage="itemsPerPageInvoicing"
                        :actions="actions"
                        tableToUse="financeTable"
                        @page-change="onPageChangeInvoicing"
                        @onOrderBy="onOrderBy"
                    >
                    </BaseTable>
                </v-window-item>
                <v-window-item :value="CustomerStep.Invoices">
                    <v-card class="border border-top-radius h-45 pt-3 " >
                        <Text class=" me-2" tag="h5" weight="medium">
                            <v-icon color="indigo" class="me-1 pb-1">mdi-file-document-outline</v-icon> {{ t("components.customers.index.issuedInvoices") }}</Text>
                    </v-card>
                    <BaseTable
                        :fetchData="null"
                        :entity="entity"
                        :items="paginatedInvoices"
                        :expandable="true"
                        expandedKey="items"
                        :expandedColumns=entity.nfeItemsTable
                        :loading="loading"
                        :totalItems="totalInvoices"
                        :currentPage="currentPageInvoices"
                        :itemsPerPage="itemsPerPageInvoices"
                        :actions="actions"
                        tableToUse="nfeTable"
                        :expandedTitle="t('components.customers.index.details')"
                        expandRowKey="id_nf"
                        buttonCleanUrl="true"
                        @page-change="onPageChangeInvoices"
                        @onOrderBy="onOrderBy">

                    </BaseTable>
                </v-window-item>
                <v-window-item :value="CustomerStep.Equipment">
                    <v-card class="border border-top-radius h-45 pt-3 " >
                        <Text class=" me-2" tag="h5" weight="medium">
                            <v-icon color="indigo" class="me-1">mdi-monitor</v-icon>
                            {{ t("components.customers.index.equipment") }}
                        </Text>
                    </v-card>
                    <BaseTable
                        :fetchData="null"
                        :entity="entity"
                        :items="paginatedEquipament"
                        :loading="loading"
                        :totalItems="totalEquipament"
                        :currentPage="currentPageEquipament"
                        :itemsPerPage="itemsPerPageEquipament"
                        :actions="actions"
                        tableToUse="comodatoTable"
                        @page-change="onPageChangeEquipament"
                        @onOrderBy="onOrderBy"
                    >
                        <template #status="{ row }">
                            <span class="trezo-status-badge" :class="computeClass(row.status)" >
                                {{ t('common.labels.' + (row.status ? 'active' : 'inactive' )) }}
                            </span>
                        </template>
                    </BaseTable>
                </v-window-item>
                <v-window-item :value="CustomerStep.Support">
                    <v-card class="border border-top-radius h-45 pt-3 " >
                        <Text class=" me-2" tag="h5" weight="medium">
                            <v-icon color="indigo" class="me-1">mdi-headset</v-icon>
                            {{ t("components.customers.index.support") }}
                        </Text>
                    </v-card>
                    <BaseTable
                        :fetchData="null"
                        :entity="entity"
                        :items="paginatedSupport"
                        :loading="loading"
                        :totalItems="totalSupport"
                        :currentPage="currentPageSupport"
                        :itemsPerPage="itemsPerPageSupport"
                        :actions="actions"
                        tableToUse="supportTable"
                        @page-change="onPageChangeSupport"
                        @onOrderBy="onOrderBy"
                    />
                </v-window-item>
                <v-window-item :value="CustomerStep.LastLogin">
                    <v-card class="border border-top-radius h-45 pt-3 ">
                        <Text class=" me-2" tag="h5" weight="medium">
                            <v-icon color="indigo" class="me-1 pb-1">mdi-account-clock</v-icon>
                            {{ t("components.customers.index.lastLogin") }}
                        </Text>
                    </v-card>

                    <v-card class="pa-4 mt-4">
                        <template v-if="lastLoginInfo">
                            <p><strong>{{ t("components.customers.index.user") }}:</strong> {{ lastLoginInfo.login }}</p>
                            <p><strong>ONU:</strong> {{ lastLoginInfo.onu_mac }}</p>
                            <p>
                                <strong>{{ t("components.customers.index.download") }}:</strong> {{ lastLoginInfo.downloadStr }}
                                <strong class="ms-4">{{ t("components.customers.index.upload") }}:</strong> {{ lastLoginInfo.uploadStr }}
                            </p>
                            <p>
                                <strong>{{ t("components.customers.index.startDate") }}:</strong> {{ lastLoginInfo.ini }}
                                <strong class="ms-4">{{ t("components.customers.index.endDate") }}:</strong> {{ lastLoginInfo.fim }}
                            </p>
                            <p>
                                <strong>{{ t("components.customers.index.cancelDate") }}:</strong> {{ lastLoginInfo.cancel }}
                            </p>
                        </template>
                        <template v-else>
                            <em>{{ t("components.customers.index.noRecord") }}</em>
                        </template>
                    </v-card>
                </v-window-item>
            </v-window>
        </template>
    </BasePage>
</template>
